// src/ui/NewGroupModal.jsx — the web's New Group sheet.
//
// Photo, name, description, and a member picker that searches the same four
// fields the app's directory search covers: name, username, phone and email
// (services/users.searchUsers already runs all four as prefix queries).
//
// Opened from Chat info as "Create group with <name>", the person you're
// talking to arrives pre-selected and can't be removed by accident — that's the
// whole premise of the entry point.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Avatar, { resolveName } from './Avatar';
import { X, Check, Search, Image as ImageIcon, Group as GroupIcon } from './icons';
import { searchUsers } from '../services/users';
import { createGroup } from '../services/groupCreate';
import { isGuestUser } from '../services/guestLinks';

export default function NewGroupModal({ me, initialMembers = [], onClose, onCreated }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [photo, setPhoto] = useState(null);
  // …including the pre-selected one: "Create group with <name>" must not be a
  // back door around the search filter when <name> is a guest.
  const [picked, setPicked] = useState(initialMembers.filter((u) => u && !isGuestUser(u)));
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const picRef = useRef(null);

  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  // name / username / phone / email — one call, four prefix queries
  useEffect(() => {
    if (!term.trim()) { setResults([]); return undefined; }
    let live = true;
    setSearching(true);
    const t = setTimeout(() => {
      searchUsers(term, me.uid)
        // a temporary guest is not group material — see isGuestUser
        .then((r) => { if (live) { setResults((r || []).filter((u) => !isGuestUser(u))); setSearching(false); } })
        .catch(() => live && setSearching(false));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [term, me.uid]);

  const pickedIds = new Set(picked.map((u) => u.uid));
  const locked = new Set(initialMembers.filter((u) => u && !isGuestUser(u)).map((u) => u.uid));

  const toggle = (u) => {
    if (isGuestUser(u)) return;                    // temporary guest — never
    if (locked.has(u.uid)) return;                 // the person this group is "with"
    setPicked((p) => (pickedIds.has(u.uid) ? p.filter((x) => x.uid !== u.uid) : [...p, u]));
  };

  const go = async () => {
    if (!name.trim() || !picked.length || busy) return;
    setBusy(true); setErr('');
    try {
      const { groupId, invited } = await createGroup({
        me, name, description: desc, imageFile: photo, members: picked,
      });
      onCreated?.(groupId, invited);
      onClose();
    } catch (e) {
      setErr('Couldn’t create the group — try again.');
      setBusy(false);
    }
  };

  return (
    <div className="ng-modal" onClick={onClose}>
      <div className="ng-card" onClick={(e) => e.stopPropagation()}>
        <div className="ng-head">
          <b><GroupIcon width={17} height={17} /> New group</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="ng-body">
          {/* identity */}
          <div className="ng-identity">
            <button className="ng-photo" onClick={() => picRef.current?.click()} title="Group photo">
              {photoUrl ? <img src={photoUrl} alt="" /> : <ImageIcon width={20} height={20} />}
              <span className="ng-photo-edit"><ImageIcon width={12} height={12} /></span>
            </button>
            <input ref={picRef} type="file" accept="image/*" hidden
              onChange={(e) => { setPhoto(e.target.files?.[0] || null); e.target.value = ''; }} />
            <div className="ng-fields">
              <input className="ng-input" value={name} maxLength={60} autoFocus
                placeholder="Group name" onChange={(e) => setName(e.target.value)} />
              <input className="ng-input" value={desc} maxLength={500}
                placeholder="Description (optional)" onChange={(e) => setDesc(e.target.value)} />
            </div>
          </div>

          {/* who's in */}
          <div className="ng-chips">
            {picked.map((u) => (
              <span className={`ng-chip ${locked.has(u.uid) ? 'locked' : ''}`} key={u.uid}>
                <Avatar profile={u} size="sm" myUid={me.uid} />
                {resolveName(u)}
                {!locked.has(u.uid) && (
                  <button onClick={() => toggle(u)} aria-label="Remove"><X width={11} height={11} /></button>
                )}
              </span>
            ))}
            {!picked.length && <span className="ng-empty">Add at least one person</span>}
          </div>

          {/* search */}
          <div className="ng-search">
            <Search width={16} height={16} />
            <input value={term} placeholder="Search by name, username, phone or email"
              onChange={(e) => setTerm(e.target.value)} />
          </div>

          <div className="ng-results">
            {searching && <div className="ci-empty">Searching…</div>}
            {!searching && term.trim() && results.length === 0 && (
              <div className="ci-empty">Nobody found</div>
            )}
            {results.map((u) => (
              <button className={`ng-row ${pickedIds.has(u.uid) ? 'on' : ''}`} key={u.uid} onClick={() => toggle(u)}>
                <Avatar profile={u} size="sm" myUid={me.uid} />
                <span className="ng-row-who">
                  <b>{resolveName(u)}</b>
                  <i>@{u.username || u.phone || u.email || 'allo'}</i>
                </span>
                {pickedIds.has(u.uid) && <Check width={15} height={15} />}
              </button>
            ))}
          </div>

          {err && <div className="pm-err">{err}</div>}
        </div>

        <div className="ng-foot">
          <span className="ng-count">{picked.length} member{picked.length === 1 ? '' : 's'}</span>
          <button className="lg-go slim" disabled={busy || !name.trim() || !picked.length} onClick={go}>
            {busy ? 'Creating…' : 'Create group'}
          </button>
        </div>
      </div>
    </div>
  );
}
